import { FieldDefinition, SelectOption, CONTEXT_FIELD_RESOLVE_TO_RESOURCE } from '../../../../../shared/components/AdvancedExpressionBuilder/types';
import { CustomField, Workflow } from '../types/dataExport.types';
import { NATIVE_FIELDS } from '../constants/dataExport.constants';
import { 
  fetchCustomFields,
  fetchCustomFieldsWithSearch,
  fetchOrganizationBranches,
  fetchSeasonIntervals,
  fetchWorkflows,
} from '../services/dataExport.api';

// Transformations
export const transformWorkflows = (workflows: any[], organizationId: string): Workflow[] => {
  return workflows.map(workflow => ({ id: workflow.id, name: workflow.title, organizationId }));
};

export const transformCustomFieldsToFieldDefinitions = (customFields: CustomField[]): FieldDefinition[] => {
  return customFields.map((cf: any) => ({
    identifier: cf.identifier,
    label: cf.label,
    fieldType: cf.fieldType as any,
    entity: cf.entity,
    resolveToResourceId: cf.identifier,
  }));
};

export const filterFieldsBySearchTerm = (fields: FieldDefinition[], searchTerm: string): FieldDefinition[] => {
  const term = searchTerm.toLowerCase();
  return fields.filter(field => field.label.toLowerCase().includes(term) || field.identifier.toLowerCase().includes(term));
};

export const buildFieldCatalog = (customFields: CustomField[], searchTerm?: string): FieldDefinition[] => {
  const mappedCustom = transformCustomFieldsToFieldDefinitions(customFields);
  const filteredNative = searchTerm ? filterFieldsBySearchTerm(NATIVE_FIELDS, searchTerm) : NATIVE_FIELDS;
  return [...filteredNative, ...mappedCustom];
};

// Field identifier prefixing
interface FieldWithEntity extends FieldDefinition { entity?: string }
export const prefixFieldIdentifiers = (fields: FieldDefinition[]): FieldDefinition[] => {
  return fields.map(field => {
    const f = field as FieldWithEntity;
    return { ...field, identifier: f.entity ? `${f.entity}_${field.identifier}` : `ticket_${field.identifier}` };
  });
};

// Resource helpers
export const isOrganizationBranchResource = (resourceId: string) => resourceId === CONTEXT_FIELD_RESOLVE_TO_RESOURCE.ORGANIZATION_BRANCH;
export const isSeasonIntervalResource = (resourceId: string) => resourceId === CONTEXT_FIELD_RESOLVE_TO_RESOURCE.SEASON_INTERVAL;

// High-level functions (logic + API calls)
export const getWorkflowsByOrganization = async (organizationId: string, signal?: AbortSignal): Promise<Workflow[]> => {
  const workflows = await fetchWorkflows(organizationId, signal);
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
  return transformWorkflows(workflows, organizationId);
};

export const getCustomFieldsByOrganization = async (organizationId: string, signal?: AbortSignal): Promise<CustomField[]> => {
  return await fetchCustomFields(organizationId, signal);
};

export const getFieldCatalogForExport = async (organizationId: string, searchTerm?: string): Promise<FieldDefinition[]> => {
  const customFields = await fetchCustomFieldsWithSearch(organizationId, searchTerm);
  return buildFieldCatalog(customFields, searchTerm);
};

export const getResourceValuesForExport = async (resourceId: string, organizationId: string, searchTerm?: string): Promise<SelectOption[]> => {
  if (!resourceId || !organizationId) return [];

  if (isOrganizationBranchResource(resourceId)) {
    const data = await fetchOrganizationBranches(organizationId);
    const options: SelectOption[] = data.map((branch: any) => ({ value: branch.id, label: branch.name }));
    return searchTerm ? options.filter(o => o.label.toLowerCase().includes(searchTerm.toLowerCase())) : options;
  }

  if (isSeasonIntervalResource(resourceId)) {
    const data = await fetchSeasonIntervals(organizationId);
    const options: SelectOption[] = data.map((season: any) => {
      const startYear = new Date(season.startDate).getFullYear().toString().slice(-2);
      const endYear = new Date(season.endDate).getFullYear().toString().slice(-2);
      return { value: season.id, label: `${startYear}/${endYear}` };
    });
    return searchTerm ? options.filter(o => o.label.toLowerCase().includes(searchTerm.toLowerCase())) : options;
  }

  const customFields = await fetchCustomFields(organizationId);
  const targetCustomField = customFields.find(cf => cf.identifier === resourceId);
  if (!targetCustomField) return [];
  const options: SelectOption[] = (targetCustomField?.components?.nodes ?? []).map((n: any) => ({
    value: n.value,
    label: n.label,
  }));
  if (searchTerm) {
    const term = searchTerm.toLowerCase();
    return options.filter(o => o.label.toLowerCase().includes(term) || String(o.value).toLowerCase().includes(term));
  }
  return options;
};

// Validation
export const validateExportRequirements = (state: any): void => {
  if (!state.selectedWorkflowId) throw new Error('Workflow must be selected');
  if (state.selectedNativeFields.length === 0 && state.selectedCustomFields.length === 0) {
    throw new Error('At least one field must be selected');
  }
};

export const validateOrganizationSelected = (organizationId: string | null): boolean => !!organizationId;

// Errors
export const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  return 'An unknown error occurred';
};

export const isAbortError = (error: unknown): boolean => error instanceof DOMException && error.name === 'AbortError';

// Files
export const downloadBlobAsFile = (blob: Blob, filename?: string): void => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  if (!filename) {
    const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    filename = `export-${ts}.csv`;
  }
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};
